<template>
  <el-container>
    <el-main>
      <h3 class="heading">New flat deal</h3>
      <el-card>
        <NewFlatDealForm
          v-model="flatDeal"
          :loading="loading"
          @submit="onSubmit"
        />
      </el-card>
      <LoadingBlock :loading="loading" />
    </el-main>
  </el-container>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue'
import { ElMessage } from 'element-plus'
import {
  FlatDealValidationErrorsKey,
  RonType,
  VideoSource,
  FlatDealType
} from '@/api/types'
import {
  IFlatDeal,
  IFlatDealCreateForm
} from '@/apps/traforama/interfaces/flatDeals'
import useRouter from '@/hooks/useRouter'
import { useFlatDealsStore } from '@/apps/traforama/store/flatDeals'
import { useCountriesStore } from '@/store/modules/countries'
import useValidation from '@/hooks/useValidation'

import LoadingBlock from '@/components/LoadingBlock.vue'
import useQueryFilters from '@/hooks/useQueryFilters'

import NewFlatDealForm from '../components/NewFlatDealForm.vue'

import { safeSubmitProcessing } from '@/utils/helpers'

export default defineComponent({
  name: 'NewFlatDeal',
  components: {
    NewFlatDealForm,
    LoadingBlock
  },
  props: {
    spotId: {
      type: String,
      default: ''
    },
    domain: {
      type: String,
      default: ''
    }
  },
  setup(props) {
    const countriesStore = useCountriesStore()
    const flatDealsStore = useFlatDealsStore()

    let flatDeal = ref<IFlatDealCreateForm | undefined>({
      // Campaign attributes
      name: '',
      deal_internal: true,
      stream: 1,
      type_id: 1,
      ron_type: RonType.Only,
      spot_ids: [],
      country_codes: [],
      device_ids: [],
      size_id: 0,
      whitelist_domains: [],
      use_whitelist_domains: true,
      domainsChanged: false,
      daily_limit: undefined,
      total_limit: undefined,
      flat_rate: undefined,
      daily_rate: undefined,
      deal_user_limit: false,
      deal_platform_id: undefined,
      subtype_id: undefined,
      // Creature attributes
      icon: '',
      image: '',
      code: '',
      impression_url: '',
      description: '',
      title: '',
      link_url: '',
      skip_offset: 5,
      vast_tag: '',
      video_source: VideoSource.Url,
      remote_url: '',
      video: '',
      deal_start: '',
      deal_stop: '',
      deal_type: undefined,
      // Helpers
      hits: 0
    })

    const { provideValidation, setValidationErrors } = useValidation(
      FlatDealValidationErrorsKey
    )
    const errors = provideValidation()
    const countries = countriesStore.items

    const loading = ref(false)
    const { router } = useRouter()

    const { getQueryFilters } = useQueryFilters({
      type_id: 'int',
      stream: 'int',
      size_id: 'int',
      hits: 'int',
      spot_id: 'int',
      domain: 'str',
      deal_platform_id: 'str',
      device_ids: 'array-int',
      countries_ids: 'array-str'
    })
    const currentQueryFilters = getQueryFilters() as any

    flatDeal.value = {
      ...flatDeal.value,
      ...currentQueryFilters,
      ...{
        ron_type: RonType.Only,
        domainsChanged: true,
        whitelist_domains: [currentQueryFilters.domain],
        spot_ids: [currentQueryFilters.spot_id],
        country_codes:
          currentQueryFilters.countries_ids?.map(
            (id: string) => countries[id]?.attributes?.iso
          ) || []
      }
    }

    const onSuccess = async (item: IFlatDeal) => {
      await router.push(`/direct_deals/${item.id}/edit?created=1`)
    }

    const onSubmit = async (flatDeal: IFlatDealCreateForm) => {
      errors.value = {}
      await safeSubmitProcessing(
        loading,
        () => flatDealsStore.SaveItem(flatDeal),
        (item: IFlatDeal) => onSuccess(item),
        (err) => setValidationErrors(err, errors)
      )
    }

    return { flatDeal, errors, loading, onSubmit }
  }
})
</script>
